/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import {
  getAllApps,
  setAppsLearnisticIntegration,
} from "../../../api/Learnistic";
import { ILearnisticApp, ILearnisticIntegration } from "../../../generated/Api";
import { notSelect } from "../../../styles/general";
import Loading from "../../Loading";
import StyledButton from "../../StyledButton";
import StyledCheckBox from "../../StyledCheckBox";
import { AllIntegrationsAtom } from "./Integrations";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 20px;
  width: 100%;
`;

const AppItem = styled.div<{ selected: boolean }>`
  height: 3.5rem;
  background-color: ${(props) => (props.selected ? "#fff2e5" : "transparent")};
  border: 1px solid ${(props) => (props.selected ? "#fb972e" : "#eceeef")};
  display: flex;
  align-items: center;
  padding: 20px;
  border-radius: 5px;
  cursor: pointer;
  ${notSelect}

  :hover {
    background-color: #fff2e5;
    border-color: #fb972e;
  }
`;

export default function LearnisticTagPicker({
  readOnly,
}: {
  readOnly?: boolean;
}) {
  const [integrations, setIntegrations] = useRecoilState(AllIntegrationsAtom);
  const learnisticIntegration = integrations?.find(
    (i) => i?.type === "LEARNISTIC"
  ) as ILearnisticIntegration;

  const [vApps, setVapps] = useState<ILearnisticApp[]>();
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const apps = await getAllApps();
      if (apps) {
        setVapps(apps);
        setSelectedApps(learnisticIntegration.args.vAppIds || []);
      }
    })();
  }, []);

  function handleChangeApp(vAppId: string) {
    if (selectedApps.includes(vAppId)) {
      setSelectedApps(selectedApps.filter((vApp) => vApp !== vAppId));
    } else if (
      learnisticIntegration.args.maxIntegrations > selectedApps.length
    ) {
      setSelectedApps([...selectedApps, vAppId]);
    }
  }

  async function setVapp() {
    try {
      if (isLoading) return;

      setIsLoading(true);
      const newLearnisticIntegration = await setAppsLearnisticIntegration({
        vAppIds: selectedApps,
      });
      setIntegrations(
        integrations.map((i) => {
          if (i.type === "LEARNISTIC")
            return { ...i, args: { ...i.args, vAppIds: selectedApps } };
          return i;
        })
      );
      toast("Success", { type: "success" });
    } catch (err) {
      console.log("Error", err);
      toast("Unexpected Error", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Container>
      {!vApps ? (
        <Loading />
      ) : readOnly ? (
        vApps
          .filter((v) => selectedApps?.includes(v.vAppId))
          .map((vApp) => {
            return (
              <AppItem style={{ cursor: "default" }} selected={true}>
                <div style={{ paddingLeft: "10px" }}>{vApp.description}</div>
              </AppItem>
            );
          })
      ) : (
        vApps.map((vApp) => {
          return (
            <AppItem
              onClick={() => handleChangeApp(vApp.vAppId)}
              selected={selectedApps?.includes(vApp.vAppId || "default")}
            >
              <StyledCheckBox
                check={selectedApps?.includes(vApp.vAppId || "default")}
                handleCheck={() => handleChangeApp(vApp.vAppId)}
              />
              <div style={{ paddingLeft: "10px" }}>{vApp.description}</div>
            </AppItem>
          );
        })
      )}
      {!readOnly && (
        <div
          style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
        >
          <StyledButton
            onClick={setVapp}
            isLoading={isLoading}
            css={css`
              width: 100px;
            `}
          >
            Save
          </StyledButton>
        </div>
      )}
    </Container>
  );
}
